/** @jsxImportSource @emotion/react */
import { useState } from 'react';

import { type ApiActivityLog, isScanHandoffable } from '@kaiizen/kai-pro-api';
import { useAuth, useFeatureFlags, useScanEditingContextSlice, useUsers, useUsersByOrgMarketId, useUsersForScan } from '@kaiizen/kai-pro-api-react-state';

import { useProjectActivityLog, useToast } from '@/Client/State';
import {
  TakeTurnModal,
  type TurnHandoffHistoryEntry,
  TurnHandoffModal,
  type TurnHandoffRecipient,
  type TurnHandoffSubmission,
} from '@/Client/Widgets';
import { MAIN_NAVIGATION_BUTTON_STYLES, type MainNavigationButtonStyles } from './mainNavigationStyles';

export const BUTTON_TEXT = 'Hand Off';
export const TAKE_WORK_BUTTON_TEXT = 'Take Work';

const TURN_HANDOFF_HISTORY_LIMIT = 2;

const getActivityLogPersonName = (name?: string, userId?: string) => name?.trim() || userId || 'Unknown person';

const isHandoffHistoryEntry = (entry: ApiActivityLog) =>
  entry.eventType === 'projectCreated'
  || entry.eventType === 'projectHandedOff'
  || entry.eventType === 'projectWorkTaken';

const getActivityLogTimestamp = (entry: ApiActivityLog) => new Date(entry.createdAt).getTime();

/** Adapts the accountability events presented in the compact handoff history. */
const getTurnHandoffHistoryEntry = (entry: ApiActivityLog): TurnHandoffHistoryEntry => {
  const description = entry.eventType === 'projectCreated'
    ? 'created the project'
    : entry.eventType === 'projectWorkTaken'
    ? 'took responsibility for the project'
    : 'handed off the project to';
  return {
    id: entry.id,
    createdAt: entry.createdAt,
    actorUserId: entry.actorUserId,
    actorName: getActivityLogPersonName(entry.actorName, entry.actorUserId),
    description,
    recipientUserId: entry.eventType === 'projectHandedOff' ? entry.aboutUserId : undefined,
    recipientName: entry.eventType === 'projectHandedOff' ? getActivityLogPersonName(entry.aboutUserName, entry.aboutUserId) : undefined,
    byUserComment: entry.byUserComment ?? entry.details,
  };
};

const compareTurnHandoffRecipients = (left: TurnHandoffRecipient, right: TurnHandoffRecipient) =>
  Number(right.isProjectParticipant) - Number(left.isProjectParticipant) || left.name.localeCompare(right.name);

/**
 * A work holder or manager can hand off a draft. Existing project members can
 * take responsibility for any active project after confirmation.
 */
export const TurnHandoffHeaderAction = ({ buttonStyles = MAIN_NAVIGATION_BUTTON_STYLES }: { buttonStyles?: MainNavigationButtonStyles }) => {
  const { userId, role, isSuperAdmin } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const { project, projectMutators, property, scan, firstScan } = useScanEditingContextSlice(['project', 'projectMutators', 'property', 'scan', 'firstScan']);
  const { users } = useUsers();
  const { users: usersByMarket } = useUsersByOrgMarketId(property?.orgMarketId ?? undefined);
  const { usersForScan: usersForCurrentScan } = useUsersForScan(scan?.id, { projectId: project?.id });
  const { usersForScan: usersForInitialScan } = useUsersForScan(firstScan?.id, { projectId: project?.id });
  const { entries } = useProjectActivityLog(project?.id, { limit: 100 });
  const { showErrorToast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [takeTurnModalOpen, setTakeTurnModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const assignedUserIds = new Set([...usersForInitialScan ?? [], ...usersForCurrentScan ?? []].map(user => user.id));
  const projectManagerIds = new Set(project?.managerIds ?? []);
  const marketUserIds = new Set((usersByMarket ?? []).map(user => user.id));
  const isProjectMember = !!userId && (assignedUserIds.has(userId) || projectManagerIds.has(userId));
  const isManager = !!userId && (projectManagerIds.has(userId) || role === 'manager' || role === 'admin' || isSuperAdmin);
  const isCurrentWorkHolder = !!userId && project?.responsibleUserId === userId;
  const canHandOff = !!project
    && !!scan
    && !!userId
    && isScanHandoffable(scan)
    && (isCurrentWorkHolder || isManager);
  const canTakeWork = !!project
    && !!scan
    && !!userId
    && project.status === 'active'
    && !project.archivedAt
    && isProjectMember
    && !isCurrentWorkHolder;

  if (!featureFlags.turnBasedHandoff || (!canHandOff && !canTakeWork) || !project || !scan) return null;

  const recipients = (users ?? [])
    .filter(user =>
      user.id !== userId
      && user.role !== 'viewer'
      && (isManager || projectManagerIds.has(user.id) || assignedUserIds.has(user.id) || marketUserIds.has(user.id))
    )
    .map(user => ({
      id: user.id,
      name: user.name?.trim() || user.email?.trim() || user.phoneNumber?.trim() || user.id,
      capabilityLabel: projectManagerIds.has(user.id)
        ? 'Can edit, approve'
        : assignedUserIds.has(user.id) || user.role === 'manager' || user.role === 'admin'
        ? 'Can edit after handoff'
        : 'Not in this project',
      isProjectParticipant: projectManagerIds.has(user.id) || assignedUserIds.has(user.id),
      willBeAddedToProject: !projectManagerIds.has(user.id) && !assignedUserIds.has(user.id),
    }))
    .sort(compareTurnHandoffRecipients);
  const history: TurnHandoffHistoryEntry[] = entries
    .filter(isHandoffHistoryEntry)
    .sort((left, right) => getActivityLogTimestamp(right) - getActivityLogTimestamp(left))
    .slice(0, TURN_HANDOFF_HISTORY_LIMIT)
    .map(getTurnHandoffHistoryEntry);
  const currentHolderName = getActivityLogPersonName(users?.find(user => user.id === project.responsibleUserId)?.name, project.responsibleUserId);

  const confirmHandoff = async ({ recipientId, byUserComment }: TurnHandoffSubmission) => {
    setSubmitting(true);
    try {
      await projectMutators.handoff(project.id, {
        scanId: scan.id,
        recipientUserId: recipientId,
        byUserComment: byUserComment || undefined,
      });
      setModalOpen(false);
    } catch (error) {
      showErrorToast(error, 'hand off this project');
    } finally {
      setSubmitting(false);
    }
  };

  const takeWork = async () => {
    setSubmitting(true);
    try {
      await projectMutators.takeWork(project.id, { scanId: scan.id });
      setTakeTurnModalOpen(false);
    } catch (error) {
      showErrorToast(error, 'take work on this project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {canTakeWork
        ? <button type='button' css={buttonStyles.secondary} disabled={submitting} onClick={() => setTakeTurnModalOpen(true)}>{TAKE_WORK_BUTTON_TEXT}</button>
        : null}
      {canHandOff ? <button type='button' css={buttonStyles.primary} disabled={submitting} onClick={() => setModalOpen(true)}>{BUTTON_TEXT}</button> : null}
      {modalOpen
        ? (
          <TurnHandoffModal
            recipients={recipients}
            history={history}
            busy={submitting}
            onConfirm={confirmHandoff}
            onCancel={() => setModalOpen(false)}
          />
        )
        : null}
      {takeTurnModalOpen
        ? <TakeTurnModal currentHolderName={currentHolderName} busy={submitting} onConfirm={takeWork} onCancel={() => setTakeTurnModalOpen(false)} />
        : null}
    </>
  );
};
